import { useCallback, useEffect, useState } from "react";
import api, { rupiah } from "@/lib/api";
import { DOMAINS } from "./domains";
import ExceptionInbox from "./ExceptionInbox";

/**
 * One view of where substantive testing stands across the four domains.
 *
 * Every figure comes from that domain's own `-summary` endpoint. Nothing here computes a metric,
 * and nothing substitutes a zero for an unknown: a cell the domain does not report shows
 * NOT_AVAILABLE, which tells the reader the source has not been supplied rather than implying the
 * work is done and came to nothing.
 *
 * A failed request is shown as a failure, not as an empty domain — the same distinction that
 * B-394 and B-398 were about.
 */

function Cell({ value, title }) {
  if (value === null || value === undefined || value === '') {
    return (
      <span className="text-muted-foreground text-xs" title={title || 'Domain ini tidak melaporkan angka ini, atau sumbernya belum disediakan'}>
        NOT_AVAILABLE
      </span>
    );
  }
  return <span>{value}</span>;
}

const GOOD = ['RECONCILED', 'MATCHED', 'OK', 'COMPLETE'];
const BAD = ['SOURCE_NOT_SUPPLIED', 'NOT_RECONCILED', 'UNRESOLVED', 'DIFFERENCE'];

function StatusPill({ status }) {
  if (!status) return <Cell value={null} />;
  const tone = GOOD.includes(status)
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : BAD.includes(status)
      ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-slate-50 text-slate-700 border-slate-200';
  return (
    <span className={`inline-block rounded border px-1.5 py-0.5 text-[11px] leading-tight ${tone}`}>
      {status}
    </span>
  );
}

export default function SubstantiveDashboard({ id, onOpenDomain }) {
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const next = {};
    await Promise.all(DOMAINS.map(async (domain) => {
      try {
        const response = await api.get(domain.summaryPath(id));
        next[domain.key] = { state: 'loaded', data: domain.adapt(response.data || {}) };
      } catch (error) {
        // A domain that failed to answer is not a domain with nothing in it.
        next[domain.key] = { state: 'failed', error };
      }
    }));
    setRows(next);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-sm text-muted-foreground" data-testid="substantive-dashboard-loading">Memuat ringkasan pengujian substantif...</div>;
  }

  const failed = DOMAINS.filter((d) => rows[d.key]?.state === 'failed');

  return (
    <div data-testid="substantive-dashboard">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="font-head font-semibold">Ringkasan Pengujian Substantif</h3>
          <p className="text-xs text-muted-foreground">
            Angka diambil langsung dari ringkasan tiap domain. Sel bertanda NOT_AVAILABLE berarti
            domain tersebut tidak melaporkan angka itu atau sumbernya belum disediakan — bukan nol.
          </p>
        </div>
        <button type="button" onClick={load} data-testid="substantive-dashboard-refresh"
                className="text-xs border rounded px-2 py-1 hover:bg-muted">
          Muat ulang
        </button>
      </div>

      {failed.length > 0 && (
        <div className="mb-3 text-sm border border-amber-200 bg-amber-50 text-amber-900 rounded px-3 py-2"
             data-testid="substantive-dashboard-error">
          {failed.length} domain gagal dimuat ({failed.map((d) => d.label).join(', ')}).
          Ini <span className="font-semibold">bukan</span> berarti domain tersebut kosong — permintaannya tidak berhasil.
        </div>
      )}

      {/* Desktop and tablet: one row per domain. */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="substantive-dashboard-table">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground border-b">
              <th className="py-2 pr-3">Domain</th>
              <th className="py-2 pr-3">Populasi</th>
              <th className="py-2 pr-3">Cakupan</th>
              <th className="py-2 pr-3">Rekonsiliasi</th>
              <th className="py-2 pr-3">Sampel</th>
              <th className="py-2 pr-3">Materialitas</th>
              <th className="py-2 pr-3">Eksepsi</th>
              <th className="py-2 pr-3" />
            </tr>
          </thead>
          <tbody>
            {DOMAINS.map((domain) => {
              const row = rows[domain.key];
              const d = row?.data;
              return (
                <tr key={domain.key} className="border-b last:border-0 align-top"
                    data-testid={`substantive-row-${domain.key}`}>
                  <td className="py-2 pr-3 font-medium">{domain.label}</td>
                  {row?.state === 'failed' ? (
                    <td colSpan={6} className="py-2 pr-3 text-amber-800 text-xs">
                      gagal dimuat — status tidak diketahui
                    </td>
                  ) : (
                    <>
                      <td className="py-2 pr-3">
                        <Cell value={d?.population != null ? `${d.population} ${d.populationUnit}` : null} />
                        {d?.populationDetail && (
                          <div className="text-[11px] text-muted-foreground">{d.populationDetail}</div>
                        )}
                      </td>
                      <td className="py-2 pr-3"><StatusPill status={d?.coverageStatus} /></td>
                      <td className="py-2 pr-3"><StatusPill status={d?.reconciliation} /></td>
                      <td className="py-2 pr-3"><Cell value={d?.sample} /></td>
                      {/* Amounts leave the API as exact strings; shown the way every
                          other screen shows money rather than as a bare figure. */}
                      <td className="py-2 pr-3">
                        <Cell value={d?.materiality != null ? rupiah(d.materiality) : null} />
                      </td>
                      <td className="py-2 pr-3"><Cell value={d?.exceptions} /></td>
                    </>
                  )}
                  <td className="py-2">
                    <button type="button" onClick={() => onOpenDomain(domain.key)}
                            data-testid={`substantive-open-${domain.key}`}
                            className="text-xs border rounded px-2 py-1 hover:bg-muted whitespace-nowrap">
                      Buka
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: review-first cards rather than a table nobody can read on a phone. */}
      <div className="md:hidden space-y-3" data-testid="substantive-dashboard-cards">
        {DOMAINS.map((domain) => {
          const row = rows[domain.key];
          const d = row?.data;
          return (
            <div key={domain.key} className="border rounded-sm p-3"
                 data-testid={`substantive-card-${domain.key}`}>
              <div className="font-medium mb-1">{domain.label}</div>
              {row?.state === 'failed' ? (
                <div className="text-xs text-amber-800">gagal dimuat — status tidak diketahui</div>
              ) : (
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <dt className="text-muted-foreground">Populasi</dt>
                  <dd><Cell value={d?.population != null ? `${d.population} ${d.populationUnit}` : null} /></dd>
                  <dt className="text-muted-foreground">Cakupan</dt>
                  <dd><StatusPill status={d?.coverageStatus} /></dd>
                  <dt className="text-muted-foreground">Rekonsiliasi</dt>
                  <dd><StatusPill status={d?.reconciliation} /></dd>
                  <dt className="text-muted-foreground">Eksepsi</dt>
                  <dd><Cell value={d?.exceptions} /></dd>
                </dl>
              )}
              <button type="button" onClick={() => onOpenDomain(domain.key)}
                      data-testid={`substantive-card-open-${domain.key}`}
                      className="mt-2 w-full text-xs border rounded px-2 py-1.5 hover:bg-muted">
                Buka {domain.label}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-6 border-t pt-4">
        <ExceptionInbox id={id} />
      </div>
    </div>
  );
}
